/**
 * ESCHER FALLS — `?debug=1` のときのポート・接続グラフ可視化オーバーレイ。
 *
 * MODULE-CONTRACTS §13。SVG を `#ui` に1枚重ねる。
 *
 * **ポートの画面位置は three.js の `Vector3.project()` ではなく
 * `core/projection.ts` の `toScreen()` から自前で計算する。**
 * こうしておくと「3D の見え方」と「接続判定に使っている数学」が食い違ったとき、
 * 円が実際のパイプ口からズレることで投影実装のバグがそのまま目に見える。
 *
 * ピクセル変換の根拠（PROJECTION-VERIFIED §4）:
 *   正射影の NDC は `ndc.x = ((P - camPos)·xAxis) / halfW`。
 *   `toScreen` は「カメラ基底との内積」そのものなので
 *   `(P - center)·xAxis === toScreen(P).x - toScreen(center).x` が厳密に成立する
 *   （`center` は注視点。カメラ方向ベクトルは xAxis / yAxis と直交するため
 *    camPos の代わりに center を使ってよく、そのほうが回転補間中も破綻しない）。
 */

import * as THREE from 'three';
import type { GameState, Vec3, ViewPose } from '../core/types';
import { RELATION_NAMES, toScreen } from '../core/projection';
import { computeBounds } from '../core/pieces';

const SVG_NS = 'http://www.w3.org/2000/svg';

const COLOR_INK = '#2b2823';
const COLOR_SOFT = '#6b665c';
const COLOR_WATER = '#4a8c8a';
const COLOR_ACCENT = '#c8622d';
const COLOR_PAPER = '#f2efe6';

const AZIMUTH_NAMES = ['SE', 'NE', 'NW', 'SW'] as const;
const ELEVATION_NAMES = ['低 A', '高 B', '真上 T'] as const;

/** HUD の左下スロット（回転ボタン）の高さぶん。テキストパネルはその上に置く。 */
const HUD_BOTTOM_RESERVE = 86;

/** location.search（省略時は現在の URL）に debug=1 が含まれるか。 */
export function isDebugEnabled(search?: string): boolean {
  const raw = search ?? (typeof location === 'undefined' ? '' : location.search);
  if (raw === '') return false;
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const value = params.get('debug');
  return value === '1' || value === 'true';
}

export interface DebugOverlay {
  update(state: GameState): void;
  setEnabled(on: boolean): void;
  dispose(): void;
}

interface Pixel {
  readonly x: number;
  readonly y: number;
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Readonly<Record<string, string | number>>,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attrs)) {
    node.setAttribute(key, String(value));
  }
  return node;
}

function svgText(x: number, y: number, text: string, fill: string, size: number): SVGTextElement {
  const node = svgEl('text', {
    x,
    y,
    fill,
    'font-size': size,
    'font-family': 'ui-monospace, SFMono-Regular, Menlo, monospace',
    'letter-spacing': '0',
  });
  node.textContent = text;
  return node;
}

/**
 * @param root    #ui 要素（この中に絶対配置の <svg> を1枚足す）
 * @param camera  scene.ts が作ったカメラ
 * @param canvas  #scene（CSS サイズをピクセル変換に使う）
 */
export function createDebugOverlay(
  root: HTMLElement,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
): DebugOverlay {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'ef-debug');
  // クリックは透過させる（§13.1: data-interactive は付けない）
  svg.style.cssText =
    'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible;';
  root.appendChild(svg);

  let enabled = true;
  /** 前回描いた内容の署名。変化が無いフレームでは DOM を作り直さない。 */
  let lastSignature = '';
  /** 直近に受け取った状態。リサイズだけで再描画するために保持する。 */
  let lastState: GameState | null = null;

  // main.ts は `subscribe` 経由で update を呼ぶので、**状態が変わらない限り呼ばれない**。
  // レイアウト確定前の初回呼び出し（clientWidth === 0）やウィンドウリサイズを
  // 自力で拾わないと、オーバーレイが描かれないまま／ズレたままになる。
  const observer =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => {
          lastSignature = '';
          if (lastState !== null) update(lastState);
        });
  observer?.observe(canvas);

  function update(state: GameState): void {
    lastState = state;
    if (!enabled) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (width <= 0 || height <= 0) return;

    const ortho = camera instanceof THREE.OrthographicCamera ? camera : null;
    const halfW = ortho === null ? 0 : (ortho.right - ortho.left) * 0.5;
    const halfH = ortho === null ? 0 : (ortho.top - ortho.bottom) * 0.5;
    // frustum がまだ設定されていない（NaN / 0）ことがある。NaN は比較が常に false に
    // なるので `<= 0` では弾けない。ここで弾かないと SVG 座標が NaN になり、
    // 全部の円が画面左上に積み上がる（実際に踏んだ）。
    const usable = ortho !== null && Number.isFinite(halfW) && Number.isFinite(halfH) && halfW > 0 && halfH > 0;

    const pose: ViewPose = state.pose;
    const ports = [...state.graph.ports.values()];
    const links = state.graph.links;

    const signature = [
      state.level.id,
      `${pose.elevation}:${pose.azimuth}`,
      state.relation,
      state.phase,
      state.goalReachable ? '1' : '0',
      width,
      height,
      usable ? `${halfW.toFixed(4)}:${halfH.toFixed(4)}` : 'unusable',
      ports.length,
      links.length,
    ].join('|');
    if (signature === lastSignature) return;
    lastSignature = signature;

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    const frag = document.createDocumentFragment();

    if (!usable) {
      // 次のフレームで frustum が入るかもしれないので、署名を残さず必ず再試行させる。
      lastSignature = '';
      frag.append(
        svgText(12, 20, 'debug: 正射影カメラの frustum が未設定（描画待ち）', COLOR_ACCENT, 12),
      );
      svg.replaceChildren(frag);
      return;
    }

    // 注視点（= frustum の中心）。scene.ts が computeBounds(pieces).center を使うのと同じ値。
    const center = computeBounds(state.pieces).center;
    const originScreen = toScreen(center, pose);

    const toPixel = (pos: Vec3): Pixel => {
      const s = toScreen(pos, pose);
      return {
        x: (((s.x - originScreen.x) / halfW) * 0.5 + 0.5) * width,
        y: ((-(s.y - originScreen.y) / halfH) * 0.5 + 0.5) * height,
      };
    };

    const pixelOf = new Map<string, Pixel>();
    for (const port of ports) pixelOf.set(port.id, toPixel(port.pos));

    const linkedPorts = new Set<string>();
    for (const link of links) {
      linkedPorts.add(link.a);
      linkedPorts.add(link.b);
    }

    // ---- 注視点の十字（投影が合っているかの基準線）--------------------------
    const c = toPixel(center);
    frag.append(
      svgEl('line', {
        x1: c.x - 7, y1: c.y, x2: c.x + 7, y2: c.y,
        stroke: COLOR_SOFT, 'stroke-width': 1, opacity: 0.5,
      }),
      svgEl('line', {
        x1: c.x, y1: c.y - 7, x2: c.x, y2: c.y + 7,
        stroke: COLOR_SOFT, 'stroke-width': 1, opacity: 0.5,
      }),
    );

    // ---- リンク（成立中の錯視接続）------------------------------------------
    // 画面上ほぼ同一点なので、線分に加えて輪で強調する（§13.1）。
    for (const link of links) {
      const pa = pixelOf.get(link.a);
      const pb = pixelOf.get(link.b);
      if (pa === undefined || pb === undefined) continue;
      frag.append(
        svgEl('line', {
          x1: pa.x, y1: pa.y, x2: pb.x, y2: pb.y,
          stroke: COLOR_WATER, 'stroke-width': 2,
        }),
        svgEl('circle', {
          cx: (pa.x + pb.x) * 0.5, cy: (pa.y + pb.y) * 0.5, r: 11,
          fill: 'none', stroke: COLOR_WATER, 'stroke-width': 1.5, 'stroke-dasharray': '3 3',
        }),
      );
    }

    // ---- ポート ---------------------------------------------------------------
    // 同じ画面位置に重なるポートはラベルを縦にずらす（重ならないと読めない）。
    const stackCount = new Map<string, number>();
    for (const port of ports) {
      const p = pixelOf.get(port.id);
      if (p === undefined) continue;
      const linked = linkedPorts.has(port.id);
      frag.append(
        svgEl('circle', {
          cx: p.x, cy: p.y, r: 4.5,
          fill: linked ? COLOR_WATER : 'none',
          stroke: linked ? COLOR_WATER : COLOR_INK,
          'stroke-width': 1.5,
        }),
      );

      const key = `${Math.round(p.x)}:${Math.round(p.y)}`;
      const index = stackCount.get(key) ?? 0;
      stackCount.set(key, index + 1);
      const label = `${port.id} ${formatPos(port.pos)}`;
      // 画面右端で切れないように、はみ出しそうなら左側へ回す（9px の等幅で概算）。
      const flip = p.x + 8 + label.length * 5.4 > width - 4;
      const text = svgText(
        flip ? p.x - 8 : p.x + 8,
        p.y - 5 - index * 11,
        label,
        linked ? COLOR_WATER : COLOR_SOFT,
        9,
      );
      if (flip) text.setAttribute('text-anchor', 'end');
      frag.append(text);
    }

    // ---- テキストパネル（左下）------------------------------------------------
    const lines = [
      // 姿勢と関係の**両方**を出す。この2つの区別が今回の設計の要であり、
      // 混同すると真上を4つと数えて検証が壊れる。
      `pose ${ELEVATION_NAMES[pose.elevation]} / ${AZIMUTH_NAMES[pose.azimuth]}`
        + `   rel ${RELATION_NAMES[state.relation]}   phase ${state.phase}`,
      `ports ${ports.length}   links ${links.length}   rot ${state.rotations}`,
      `この視点で解ける: ${state.goalReachable ? 'YES' : 'no'}`,
      flowSummary(state),
    ];
    const panelH = lines.length * 14 + 12;
    const longest = lines.reduce((n, line) => Math.max(n, line.length), 0);
    const panelW = Math.min(width - 20, longest * 6.4 + 20);
    // HUD の左下（回転ボタン）を隠さないよう、その上に載せる。
    const panelY = Math.max(10, height - panelH - HUD_BOTTOM_RESERVE);
    frag.append(
      svgEl('rect', {
        x: 10, y: panelY, width: panelW, height: panelH,
        fill: COLOR_PAPER, opacity: 0.82, stroke: COLOR_INK, 'stroke-width': 1,
      }),
    );
    for (let i = 0; i < lines.length; i += 1) {
      frag.append(svgText(20, panelY + 20 + i * 14, lines[i], COLOR_INK, 11));
    }

    svg.replaceChildren(frag);
  }

  function setEnabled(on: boolean): void {
    enabled = on;
    svg.style.display = on ? '' : 'none';
    // 無効化中は update をスキップしているので、再開時は必ず描き直させる。
    if (on) lastSignature = '';
  }

  function dispose(): void {
    observer?.disconnect();
    lastState = null;
    svg.remove();
  }

  return { update, setEnabled, dispose };
}

function formatPos(pos: Vec3): string {
  return `(${trim(pos[0])},${trim(pos[1])},${trim(pos[2])})`;
}

function trim(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function flowSummary(state: GameState): string {
  const plan = state.flow;
  if (plan === null) return 'flow  —';
  const goal = plan.goalTime === null ? '—' : `${Math.round(plan.goalTime)}ms`;
  return `flow  ${Math.round(state.flowTime)}/${Math.round(plan.durationMs)}ms  goal ${goal}  cells ${plan.totalCells}${plan.budgetExceeded ? ' (budget切れ)' : ''}`;
}
